/**
 * Phase 5.9 — Page view tracker.
 *
 * Fires a `page.viewed` event to the customer_events warehouse every time the
 * authenticated user lands on a new pathname. Includes the route name and the
 * document.referrer (only for the very first hit of the session — internal
 * navigations use the previous pathname instead, so dispatchers can see the
 * funnel: /customer → /customer/cart → /customer/checkout etc.).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent } from '../utils/eventsLogger';

const ROUTE_LABELS = {
  '/customer': 'customer_dashboard',
  '/customer/cart': 'customer_cart',
  '/customer/orders': 'customer_orders',
  '/customer/profile': 'customer_profile',
  '/customer/refer': 'customer_refer',
  '/driver': 'driver_dashboard',
  '/driver/profile': 'driver_profile',
  '/dispatch': 'dispatch_dashboard',
  '/dispatch/settings': 'dispatch_settings',
  '/dispatch/broadcast': 'dispatch_broadcast',
  '/dispatch/inventory': 'dispatch_inventory',
  '/dispatch/admin/points': 'dispatch_admin_points',
  '/dispatch/admin/users': 'dispatch_admin_users',
  '/dispatch/admin/regions': 'dispatch_admin_regions',
  '/dispatch/admin/leaderboards': 'dispatch_admin_leaderboards',
  '/staff/rewards': 'staff_rewards',
  '/login': 'login',
  '/register': 'register',
};

function labelFor(pathname) {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  // /customer/orders/:id → customer_order_detail
  if (/^\/customer\/orders\/[^/]+$/.test(pathname)) return 'customer_order_detail';
  return pathname.replace(/^\//, '').replace(/\//g, '_') || 'root';
}

export function usePageViewTracker(isAuthed) {
  const location = useLocation();
  const prevRef = useRef(null);

  useEffect(() => {
    if (!isAuthed) return;
    const path = location.pathname;
    const prev = prevRef.current;
    // Skip if same path (e.g. React StrictMode double-render in dev)
    if (prev === path) return;

    logEvent('page.viewed', {
      path,
      route: labelFor(path),
      referrer: prev || (typeof document !== 'undefined' ? document.referrer : '') || null,
    }, { dedupeKey: path });

    prevRef.current = path;
  }, [location.pathname, isAuthed]);
}
